import { useState, useEffect } from 'react';
import { fetchCharacters } from '@components';
import { Utils } from '@client/utility';
import { usePyrenzAlert } from '@renderer/provider';
import { GetUserUUID } from '@client/components';
import type { Character } from '@shared-types';

export const useCharacterData = (char_uuid: string) => {
  const [character, setCharacter] = useState<Character | null>(null);
  const [recommendedCharacters, setRecommendedCharacters] = useState<Character[]>([]);
  const [loading, setLoading] = useState(true);
  const [userUuid, setUserUuid] = useState<string | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const showAlert = usePyrenzAlert();

  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      const uuid = await GetUserUUID();
      if (!isMounted) return;
      
      setUserUuid(uuid);
      setIsLoggedIn(!!uuid);

      if (!char_uuid) {
        setLoading(false);
        return;
      }

      try {
        const { characters, recommendedCharacters } = await fetchCharacters({
          currentPage: 1,
          itemsPerPage: 1,
          sortBy: 'chat_messages_count',
          filterCharUUID: char_uuid,
          filterCreatorUUID: uuid,
          showRecommended: true,
        });

        if (!isMounted) return;
        setCharacter(characters?.[0] ?? null);
        setRecommendedCharacters(recommendedCharacters ?? []);
      } catch (err) {
        if (isMounted) console.error('💥 Error fetching character:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    init();

    return () => {
      isMounted = false;
    };
  }, [char_uuid]);

  const handleDeleteCharacter = async (): Promise<boolean> => {
    if (!userUuid) return false;

    try {
      const deleted = await Utils.db.remove<{ char_uuid: string }>({
        tables: ['public_characters', 'private_characters'],
        match: { char_uuid },
      });

      if ((deleted?.length ?? 0) > 0) {
        showAlert('Character deleted successfully', 'success');
        setCharacter(null);
        return true;
      }

      showAlert('Character could not be deleted', 'error');
      return false;
    } catch (err) {
      console.error('🚫 Failed to delete character:', err);
      showAlert('Error deleting character', 'error');
      return false;
    }
  };

  const handleReportCharacter = async (
    reportText: string,
    creator_uuid: string
  ): Promise<boolean> => {
    if (!userUuid) return false;

    try {
      const result = await Utils.db.insert({
        tables: 'character_reports',
        data: { user_uuid: userUuid, char_uuid, creator_uuid, report_content: reportText },
        options: { onConflict: ['user_uuid', 'char_uuid'] },
      });

      if (Array.isArray(result) && result.length > 0) {
        showAlert('Your report has been submitted.', 'success');
        return true;
      }

      showAlert('Could not submit report.', 'error');
      return false;
    } catch (err) {
      console.error('Failed to report character:', err);
      showAlert('An error occurred while reporting.', 'error');
      return false;
    }
  };

  return {
    character,
    recommendedCharacters,
    loading,
    userUuid,
    isLoggedIn,
    handleDeleteCharacter,
    handleReportCharacter,
  };
};